import { useCallback, useMemo } from 'react';
import { useEffect, useState } from 'react';

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AuthApi } from './authApi';
import { PublicCatalogApi } from './catalogApi';
import { AuthUser, LoginCredentials, PublicDefinition, RegisterCredentials } from './schemas';
import { SessionStatus, useSessionStore } from './sessionStore';

/** convenient session snapshot (Zustand selector) */
export function useSession(): { status: SessionStatus; user: AuthUser | null; isLoggedIn: boolean } {
  const status = useSessionStore((s) => s.status);
  const user = useSessionStore((s) => s.user);
  return { status, user, isLoggedIn: status === 'authenticated' };
}

/** login mutation (TanStack Query — not SWR/Redux [STD-FE]) — returns LoginResult (auth veya mfa). */
export function useLogin(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'login'],
    mutationFn: (credentials: LoginCredentials) => authApi.login(credentials)
  });
}

/** MFA 2. adım mutation'ı — challenge token + kod → oturum. */
export function useCompleteMfa(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'mfa'],
    mutationFn: ({ challengeToken, code }: { challengeToken: string; code: string }) =>
      authApi.completeMfa(challengeToken, code)
  });
}

export function useLogout(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'logout'],
    mutationFn: () => authApi.logout()
  });
}

/** SR: self-registration mutation — resolves once the verification email is queued. */
export function useRegister(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'register'],
    mutationFn: (credentials: RegisterCredentials) => authApi.register(credentials)
  });
}

/**
 * RSU: the partner companies a visitor can pick on the signup form. Anonymous, so it is safe to run
 * before any session exists. `enabled` lets a page that does not need it (the student form when the
 * tenant has no partners) skip the request entirely.
 */
/**
 * BEMORE: the roles this tenant lets a visitor APPLY for.
 *
 * ⚠ AN EMPTY ARRAY IS A REAL ANSWER, not a loading state: a tenant that has built no form offers no
 * Be More door, and the chooser must not show an option that leads to an empty dropdown. Callers
 * decide on `data?.length`, never on `isSuccess` alone.
 *
 * Same 5-minute staleTime as the partner list, and for the same reason: stepping from the chooser
 * into the form and back must not refetch a catalogue that changes when an admin edits it, not when
 * a visitor navigates.
 */
export function useRoleApplicationForms(authApi: AuthApi, enabled = true) {
  return useQuery({
    queryKey: ['auth', 'role-application-forms'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: () => authApi.listRoleApplicationForms()
  });
}

export function usePublicPartners(authApi: AuthApi, enabled = true) {
  return useQuery({
    queryKey: ['auth', 'public-partners'],
    enabled,
    // Business names change rarely; a stale-time keeps the chooser from refetching on every
    // role switch inside one signup session.
    staleTime: 5 * 60 * 1000,
    queryFn: () => authApi.listPublicPartners()
  });
}

/**
 * SIGNUP-SCHOOL: the schools a visitor can pick, in the country they chose.
 *
 * ⚠ KEYED ON THE COUNTRY and disabled without one. Both matter: the server answers EMPTY for a missing
 * country (it is anonymous and will not enumerate a tenant's institutions), so an unkeyed query would
 * cache that empty answer and keep serving it after a country IS chosen.
 */
/**
 * SIGNUP-SEARCH — the cap the server applies (ListPublicSchoolsQueryHandler.Cap). A full page means
 * "there are more, keep typing", which is the only thing the UI needs it for.
 *
 * ⚠ MIRRORED, NOT FETCHED, and that is a deliberate trade: the response is a bare array with nowhere
 * to put a `truncated` flag, and widening it would change an anonymous contract for a hint. If the two
 * ever drift the cost is a sentence that shows when it needn't, or doesn't when it should — never a
 * wrong school or a missing one.
 */
export const PUBLIC_SCHOOL_LIST_CAP = 50;

export function usePublicSchools(
  authApi: AuthApi,
  countryDefinitionId: string | undefined,
  search?: string
) {
  const term = (search ?? '').trim();
  return useQuery({
    // ⚠ THE TERM IS PART OF THE KEY. Without it every search would read back the first page from
    // cache and the box would look like it filtered nothing — the same defect the admin list pages
    // hit when a filter was left out of its queryKey.
    queryKey: ['auth', 'public-schools', countryDefinitionId ?? '', term],
    enabled: Boolean(countryDefinitionId),
    // Institutions change rarely; matches usePublicPartners so stepping back to the role chooser and
    // into the student form again does not refetch.
    staleTime: 5 * 60 * 1000,
    // Keeps the previous page on screen while a new term is in flight, so the list does not blank out
    // between keystrokes — the visitor sees it narrow rather than flicker.
    placeholderData: (prev) => prev,
    queryFn: () => authApi.listPublicSchools(countryDefinitionId!, term || undefined)
  });
}

/**
 * SIGNUP-COUNTRY: the countries a visitor can pick on the signup form. Anonymous, so it is safe to run
 * before any session exists — and it hits the **Definitions** service, not Identity (see catalogApi).
 *
 * The stale-time matches usePublicPartners for the same reason: a visitor who steps back to the role
 * chooser and into a different form must not re-fetch a reference list that changes a few times a year.
 */
export function usePublicCountries(catalogApi: PublicCatalogApi) {
  return useQuery({
    queryKey: ['auth', 'public-countries'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => catalogApi.listPublicCountries()
  });
}

/**
 * SIGNUP-GENDER — the `genders` option list behind the signup gender picker.
 *
 * ⚠ NOT GATED on an `enabled` flag, unlike usePublicGeographyLabels below. That gate exists because
 * districts are ~101 kB; this group is four seeded rows, and a gate for four rows would only add a
 * state the picker can be wrong about. Same 5-minute stale time as the country list, and for the same
 * reason — a visitor stepping back to the role chooser and into the form again must not refetch a
 * table that changes about never.
 */
export function usePublicGenders(catalogApi: PublicCatalogApi) {
  return useQuery({
    queryKey: ['auth', 'public-genders'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => catalogApi.listPublicGenders()
  });
}

/**
 * SIGNUP-LABEL — id → name for cities and districts, so the school picker can show
 * "Name / City / District". A school row carries only the opaque ids (Identity cannot read the
 * definitions schema [STD-OWN]), and without this a visitor choosing between 120 schools called
 * "Atatürk Anadolu Lisesi" has nothing to choose ON.
 *
 * ⚠ GATED ON `enabled`, and the gate matters more than it looks. Districts are ~101 kB on a tenant
 * holding an imported national register; a teacher or partner-employee signup has no school field at
 * all, and neither does the form before a country is picked. Fetching it on mount would put that on
 * every visitor to every signup form for a label most of them never see.
 *
 * The stale time matches the other reference lists: a visitor stepping back to the role chooser and
 * into the student form again must not re-fetch a table that changes a few times a year.
 */
/** Stable identity for "not loaded yet" — a fresh `[]` per render would defeat the memo below. */
const EMPTY: PublicDefinition[] = [];

export function usePublicGeographyLabels(catalogApi: PublicCatalogApi, enabled: boolean) {
  const cities = useQuery({
    queryKey: ['auth', 'public-cities'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: () => catalogApi.listPublicCities()
  });
  const districts = useQuery({
    queryKey: ['auth', 'public-districts'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: () => catalogApi.listPublicDistricts()
  });

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of cities.data ?? []) map.set(d.id, d.name);
    for (const d of districts.data ?? []) map.set(d.id, d.name);
    return map;
  }, [cities.data, districts.data]);

  /**
   * "Name / City / District", dropping the parts that are missing.
   *
   * ⚠ MISSING COVERS TWO DIFFERENT THINGS and both must read the same: the school genuinely has no
   * district, and the lists have not loaded yet. Rendering a placeholder for the second would make
   * every label flicker through "Name / … / …" on first paint; rendering the bare name and letting
   * the rest appear is the honest degradation.
   */
  const label = useCallback(
    (school: { name: string; cityDefinitionId?: string | null; districtDefinitionId?: string | null }) =>
      [school.name, nameById.get(school.cityDefinitionId ?? ''), nameById.get(school.districtDefinitionId ?? '')]
        .filter(Boolean)
        .join(' / '),
    [nameById]
  );

  // ⚠ THE RETURN VALUE IS MEMOIZED, and that is not tidiness. A caller memoizes its option list on
  // this object; a fresh one every render silently defeats that memo, and the symptom is a school
  // dropdown that rebuilds all 50 labels on every keystroke while looking perfectly correct. Nothing
  // in typecheck or lint objects to it.
  // SIGNUP-SCHOOL-NEW: the same two lists, unlabelled, for the pickers a visitor uses to DESCRIBE a
  // school that is not registered yet. No second request — react-query serves both callers from the
  // query keys above, which is the reason this hook grew the lists instead of a sibling hook fetching
  // them again for the panel.
  //
  // ⚠ FILTERED BY PARENT AT THE CALL SITE, not here: every city carries its country and every district
  // its city (verified on the dev tenant: 82 cities and 782 districts, all with a parent), and which
  // parent applies is the caller's question, not this hook's.
  return useMemo(
    () => ({
      label,
      isLoading: cities.isLoading || districts.isLoading,
      cities: cities.data ?? EMPTY,
      districts: districts.data ?? EMPTY
    }),
    [label, cities.isLoading, districts.isLoading, cities.data, districts.data]
  );
}

/** SR: email-verification mutation — token from the ?token= link. */
export function useVerifyEmail(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'verify-email'],
    mutationFn: (token: string) => authApi.verifyEmail(token)
  });
}

/** SR: resend the verification email for an address. */
export function useResendVerification(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'verify-email', 'resend'],
    mutationFn: (email: string) => authApi.resendVerification(email)
  });
}

/**
 * TCH-INV: describe an activation link. A QUERY (not a mutation) because it is a read, but with
 * retry off and no caching across tokens — a consumed link must not keep looking valid.
 */
export function useActivationInfo(authApi: AuthApi, token: string | undefined) {
  return useQuery({
    queryKey: ['auth', 'activation', token],
    enabled: Boolean(token),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: () => authApi.activationInfo(token!)
  });
}

/** TCH-INV: consume the activation link, setting the invitee's own first password. */
export function useCompleteActivation(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'activation', 'complete'],
    mutationFn: (vars: { token: string; password: string }) =>
      authApi.completeActivation(vars.token, vars.password)
  });
}

/**
 * PWD-RESET: ask for a reset link.
 *
 * ⚠ `isSuccess` means the REQUEST was accepted, NOT that mail went out — the server answers
 * identically for an address with no account. Copy driven off this must say what the user should do
 * next, never assert that a message is on its way to them.
 */
export function useRequestPasswordReset(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'password-reset', 'request'],
    mutationFn: (email: string) => authApi.requestPasswordReset(email)
  });
}

/**
 * PWD-RESET: describe a reset link. A QUERY (it is a read) but with retry off and NO caching across
 * tokens — a consumed link must not keep looking valid, which is the same trap the server side had to
 * fix with [NoCache].
 */
export function usePasswordResetInfo(authApi: AuthApi, token: string | undefined) {
  return useQuery({
    queryKey: ['auth', 'password-reset', token],
    enabled: Boolean(token),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: () => authApi.passwordResetInfo(token!)
  });
}

/** PWD-RESET: consume the reset link, replacing the password and ending every session. */
export function useCompletePasswordReset(authApi: AuthApi) {
  return useMutation({
    mutationKey: ['auth', 'password-reset', 'complete'],
    mutationFn: (vars: { token: string; password: string }) =>
      authApi.completePasswordReset(vars.token, vars.password)
  });
}

/** current user query — enabled only while authenticated */
export function useMe(authApi: AuthApi) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
    enabled: isLoggedIn,
    staleTime: 60 * 1000
  });
}

/** SCH-independent: the partner companies the current user may assign to themselves — enabled only
 *  while authenticated (self-gated endpoint). */
export function useAssignablePartners(authApi: AuthApi) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: ['auth', 'assignable-partners'],
    queryFn: () => authApi.getAssignablePartners(),
    enabled: isLoggedIn,
    staleTime: 60 * 1000
  });
}

/** SCH-independent: set the current user's own partner (null clears it). Refreshes `me` so the
 *  resolved partnerName updates. */
export function useSetMyPartner(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-partner'],
    mutationFn: (companyId: string | null) => authApi.setMyPartner(companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/** PPT-2: the caller's per-program-type partner choices.
 *
 *  ⚠ NO staleTime. Every other self picker here caches for 60s, but this list is the one a student
 *  edits repeatedly in a row (one row per program type) — a stale window means row 2's save appears
 *  to revert row 1. The writes invalidate it explicitly, so the extra fetch only happens after a
 *  change anyway. */
export function useMyPartnerSelections(authApi: AuthApi) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: ['auth', 'partner-selections'],
    queryFn: () => authApi.getMyPartnerSelections(),
    enabled: isLoggedIn
  });
}

/** PPT-2: the live partners supporting ONE program type — the filtered picker options. */
export function useAssignablePartnersForType(authApi: AuthApi, programTypeId: string | undefined) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: ['auth', 'assignable-partners', programTypeId],
    queryFn: () => authApi.getAssignablePartnersForType(programTypeId as string),
    enabled: isLoggedIn && Boolean(programTypeId),
    staleTime: 60 * 1000
  });
}

/** PPT-SCH: what the caller's own school routes each program type to.
 *
 *  ⚠ [NoCache] server-side and no staleTime here: it changes when the caller's SCHOOL changes, and the
 *  school picker sits on the very same screen — a cached answer would explain a freshly inherited
 *  partner with the PREVIOUS school's routing. */
export function useMySchoolPartnerSelections(authApi: AuthApi) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: ['auth', 'school-partner-selections'],
    queryFn: () => authApi.getMySchoolPartnerSelections(),
    enabled: isLoggedIn
  });
}

/** PPT-2: set the caller's partner for one program type (null clears).
 *
 *  Invalidates the selections list AND `me`: the partner choice is an ACCESS EDGE (it decides which
 *  partner employees can see this student), so anything projecting it must re-read rather than keep
 *  showing the pre-change answer. */
export function useSetMyPartnerSelection(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-partner-selection'],
    mutationFn: (vars: { programTypeId: string; companyId: string | null }) =>
      authApi.setMyPartnerSelection(vars.programTypeId, vars.companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'partner-selections'] });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/** SP: the users the current user may pick as their salesperson — enabled only while authenticated. */
export function useSalespersonOptions(authApi: AuthApi) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: ['auth', 'salesperson-options'],
    queryFn: () => authApi.getSalespersonOptions(),
    enabled: isLoggedIn,
    staleTime: 60 * 1000
  });
}

/** SP: set the current user's own salesperson (null clears it). Refreshes `me` so the resolved
 *  salespersonName updates. */
export function useSetMySalesperson(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-salesperson'],
    mutationFn: (userId: string | null) => authApi.setMySalesperson(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/** STU-TCH: the 'teacher'-role users the current user may pick as their teacher. */
export function useTeacherOptions(authApi: AuthApi) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: ['auth', 'teacher-options'],
    queryFn: () => authApi.getTeacherOptions(),
    enabled: isLoggedIn,
    staleTime: 60 * 1000
  });
}

/** STU-TCH: set the current user's own teacher (null clears it). Refreshes `me` so the resolved
 *  teacherName updates — the profile shows the NAME, never the raw id. */
export function useSetMyTeacher(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-teacher'],
    mutationFn: (userId: string | null) => authApi.setMyTeacher(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/** SELF-SCHOOL: the live schools the caller may pick. Its own endpoint because GET /schools needs
 *  identity.schools.view, which a student does not hold. */
/**
 * SELF-CTRY: the query key of the school picker's options, exported because a CALLER has to invalidate it.
 *
 * ⚠ THIS LIST DEPENDS ON THE CALLER'S OWN COUNTRY (the server narrows it to schools in it), so saving the
 * demographics form makes it stale — and nothing in this package can know that happened. Both Profile pages
 * therefore invalidate this key after that save; without it the picker keeps the previous country's schools
 * until a page reload, which is exactly how it was reported.
 */
export const SCHOOL_OPTIONS_QUERY_KEY = ['auth', 'school-options'] as const;

/**
 * SCH-PICK: rows the server returns at most (`GetMySchoolOptionsQueryHandler.Cap`). Exported because a
 * FULL list is the client's only cue that there is more — "50 of them, keep typing" — and a page that
 * showed 50 of 11,779 silently would read as a complete catalogue.
 */
export const SCHOOL_OPTION_CAP = 50;

/**
 * SCH-PICK: below this many characters the term is NOT sent and the server's first page is shown
 * instead. A one-character term cannot use the trigram index behind `search_name`, so it is a
 * sequential scan of the whole register on every keystroke.
 */
export const SCHOOL_SEARCH_MIN_LENGTH = 2;

/**
 * The school picker's options, SEARCHED ON THE SERVER.
 *
 * ⚠ WHY IT TAKES A TERM AT ALL: this endpoint used to answer with every live school in the caller's
 * country. That was fine while a tenant had a dozen and stopped being fine when the MEB register landed —
 * 11,779 rows, fetched on every profile page load, into a dropdown that could only be scrolled. The
 * server now searches (accent-folded) and caps at {@link SCHOOL_OPTION_CAP}.
 *
 * ⚠ THE TERM IS DEBOUNCED HERE, NOT IN THE PAGES. Undebounced, every keystroke is a request and the
 * answers race — the 4-character reply can land after the 5-character one and repopulate the list with
 * the wider result. Both Profile pages would have had to remember that separately.
 *
 * ⚠ AND THE PREVIOUS PAGE IS KEPT WHILE THE NEXT LOADS (`placeholderData`). Without it the list empties
 * between terms and the picker flashes "no results" at a user who is still typing.
 */
export function useSchoolOptions(authApi: AuthApi, search?: string) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  const term = (search ?? '').trim();
  const [settled, setSettled] = useState(term);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(term), 300);
    return () => clearTimeout(timer);
  }, [term]);

  const effective = settled.length >= SCHOOL_SEARCH_MIN_LENGTH ? settled : '';

  return useQuery({
    // ⚠ THE TERM IS IN THE KEY, APPENDED to the exported base key — so a caller invalidating
    // SCHOOL_OPTIONS_QUERY_KEY after a country change still clears every term (TanStack matches key
    // PREFIXES), which is the behaviour both Profile pages already depend on.
    queryKey: [...SCHOOL_OPTIONS_QUERY_KEY, effective],
    queryFn: () => authApi.getSchoolOptions(effective || undefined),
    enabled: isLoggedIn,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData
  });
}

/** SELF-SCHOOL: set the caller's own school (null clears). Refreshes `me` for the resolved name. */
export function useSetMySchool(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-school'],
    mutationFn: (schoolId: string | null) => authApi.setMySchool(schoolId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      // ⚠ PPT-SCH (mig 329): choosing a school now WRITES partner selections — the school's per-type
      // partners are inherited for every type the student has not chosen for. Without these two the
      // partner pickers keep showing the pre-school answer until a manual reload, so the student sees
      // nothing happen and the feature looks broken. The routing list drives the "from your school"
      // hint and changes with the school itself, so it has to go too.
      void queryClient.invalidateQueries({ queryKey: ['auth', 'partner-selections'] });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'school-partner-selections'] });
    }
  });
}

/** SELF-TEENEAGLE-PARENT: selectable users — ANY live user but the caller (no 'parent' role exists).
 *  ⚠ The server CAPS this list, so a full result may be truncated; the UI must say so. */
export function useTeeneagleParentOptions(authApi: AuthApi) {
  const isLoggedIn = useSessionStore((s) => s.status === 'authenticated');
  return useQuery({
    queryKey: ['auth', 'teeneagle-parent-options'],
    queryFn: () => authApi.getTeeneagleParentOptions(),
    enabled: isLoggedIn,
    staleTime: 60 * 1000
  });
}

/**
 * AVATAR-UPLOAD: the caller's avatar as an object URL, or null when there is no upload.
 *
 * ⚠ An <img src> cannot carry an Authorization header, so the bytes are FETCHED and wrapped in an object
 * URL. Those URLs are a manual resource: the previous one is revoked whenever a new blob arrives and on
 * unmount, otherwise every avatar change leaks the old image for the life of the document.
 *
 * Keyed on `hasAvatar` from /me so it re-fetches after an upload or a delete without the caller
 * orchestrating anything.
 */
export function useMyAvatarUrl(authApi: AuthApi, hasAvatar: boolean | null | undefined) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    // No synchronous setState here: clearing on !hasAvatar is DERIVED below instead, because setting
    // state inline in an effect triggers a cascading render (and the lint rule that says so is right).
    if (!hasAvatar) return undefined;

    let cancelled = false;
    let created: string | null = null;

    void authApi
      .getMyAvatarBlob()
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      // A 404 here is normal, not exceptional: /me can say hasAvatar while the object has just been
      // removed by another tab. Fall back to no picture rather than surfacing an error.
      .catch(() => {
        if (!cancelled) setUrl(null);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [authApi, hasAvatar]);

  // Derived, not stored: with no upload the answer is null whatever stale object URL sits in state.
  return hasAvatar ? url : null;
}

/** AVATAR-UPLOAD: upload (replaces any existing image). Refreshes `me` so hasAvatar flips. */
export function useUploadMyAvatar(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'upload-my-avatar'],
    mutationFn: (file: File) => authApi.uploadMyAvatar(file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/** AVATAR-UPLOAD: remove the uploaded image (any legacy avatarUrl is untouched). */
export function useDeleteMyAvatar(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'delete-my-avatar'],
    mutationFn: () => authApi.deleteMyAvatar(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/** Guardian e-mail / phone. Its own endpoint so the demographics form and this one never write the
 *  same field — see authApi's myGuardianContact note. */
export function useSetMyGuardianContact(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-guardian-contact'],
    mutationFn: (contact: { parentGuardianEmail: string | null; parentGuardianPhone: string | null }) =>
      authApi.setMyGuardianContact(contact),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/** USR-NAME: set / change / release the caller's handle. Null or blank RELEASES it. */
export function useSetMyUsername(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-username'],
    mutationFn: (username: string | null) => authApi.setMyUsername(username),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/**
 * STU-HANDLE (mig 314): set or replace the caller's own address.
 *
 * ⚠ Re-reading `/me` on success is REQUIRED, not a nicety. The write resets `emailVerified` to false and the
 * "add an address" banner is driven by that field — without the invalidation the banner keeps rendering the
 * pre-write answer, which for a student who just complied is the same defect twice over (they added an
 * address and are still being told to). The server's [InvalidatesCache] clears Redis; this clears the client
 * cache. Both are needed, and neither substitutes for the other.
 */
export function useSetMyEmail(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-email'],
    mutationFn: (email: string) => authApi.setMyEmail(email),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/**
 * USR-NAME: as-you-type availability for a candidate handle.
 *
 * ⚠ TWO things this must get right, both invisible to a type checker:
 *
 * 1. RACING. Answers can arrive out of order while the user keeps typing, so a slow reply for 'ahm' must
 *    not overwrite a fast reply for 'ahmet'. The result is only accepted when the echoed handle matches
 *    what is being asked about NOW — which is exactly why the server echoes the normalised handle back.
 * 2. NOT ASKING TOO EARLY. A malformed candidate is a 400, not "unavailable"; probing every keystroke
 *    would light the field red while someone is halfway through typing a legal handle. The caller passes
 *    `enabled` and only turns it on once the value is plausibly complete.
 *
 * The endpoint is deliberately [NoCache] server-side — a cached "available" would outlive someone else
 * claiming the handle. TanStack still de-dupes identical in-flight requests.
 */
export function useUsernameAvailability(authApi: AuthApi, candidate: string, enabled: boolean) {
  const normalized = candidate.trim().toLowerCase();
  const query = useQuery({
    queryKey: ['auth', 'username-available', normalized],
    queryFn: () => authApi.checkUsernameAvailability(normalized),
    enabled: enabled && normalized.length > 0,
    // Short but non-zero: retyping the same handle within a few seconds should not re-ask, while a handle
    // someone else claims is re-checked on the next edit.
    staleTime: 5_000,
    retry: false
  });

  // Drop an answer that belongs to a different (earlier) handle.
  const fresh = query.data?.username === normalized ? query.data : undefined;

  return {
    /** true = free, false = taken, undefined = unknown (loading, disabled, malformed, or stale). */
    available: fresh?.available,
    isChecking: query.isFetching,
    /** A 400 means the shape is wrong; the field's own validation message says what is legal. */
    isMalformed: query.isError
  };
}

/** SELF-TEENEAGLE-PARENT: set the caller's own Teeneagle parent (null clears). */
export function useSetMyTeeneagleParent(authApi: AuthApi) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['auth', 'set-my-teeneagle-parent'],
    mutationFn: (userId: string | null) => authApi.setMyTeeneagleParent(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}
